/**
 * react-i18next instance for the web SPA (ISSUE-125 §1.1).
 *
 * Import this module at the top of main.tsx (before React renders) so the
 * instance is ready when components first mount. The instance is also exported
 * so callers can imperatively change the language on locale toggle.
 */

import { DEFAULT_LOCALE, FALLBACK_LOCALE, resolveLocale, resources } from '@nutians/shared/i18n';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ICU from 'i18next-icu';
import { initReactI18next } from 'react-i18next';

i18next
  .use(ICU)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: FALLBACK_LOCALE,
    // LanguageDetector order: localStorage → navigator (§2.2)
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'nutians_locale',
      caches: ['localStorage'],
    },
    load: 'currentOnly',
    ns: [
      'common',
      'settings',
      'email',
      'log',
      'coach',
      'paywall',
      'progress',
      'landing',
      'push',
      'cycle',
      'sharing',
    ],
    defaultNS: 'common',
    interpolation: { escapeValue: true },
  });

/**
 * Switch to `locale` persistently. Call after the user picks a language in
 * Settings (also updates localStorage via LanguageDetector + persists to the
 * server through the profile PATCH in the Settings component).
 */
export async function setLocale(locale: string): Promise<void> {
  const resolved = resolveLocale(locale);
  localStorage.setItem('nutians_locale', resolved);
  await i18next.changeLanguage(resolved);
}

export { DEFAULT_LOCALE, i18next };
